import { useEffect, useState } from 'react';

import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import { HeaderRegion } from '../../../data/types';
import { globalVariables } from '../../../shared/designSystem/constants';

// PST-570 — PC 헤더 해외/국내 pill 토글 (presentational).
// 모바일 웹 RegionToggle 과 시각적으로 동일하게 맞춘다(4-state 매트릭스·색·사이즈·폰트 비대칭 그대로).
// 네비게이션/로깅은 소비처가 onChange 로 주입한다.
// badgeText 주입 시, 해외 selected 상태에서 국내 chip 아래 안내 말풍선을 노출한다(모바일 동일).

interface RegionOption {
  label: string;
  value: HeaderRegion;
}

const REGION_OPTIONS: RegionOption[] = [
  { label: '해외', value: 'overseas' },
  { label: '국내', value: 'domestic' },
];

/** 국내 정체성 그라데이션 — 89.99° #1B99FF → #7018FF (모바일 RegionToggle 정본과 동일). */
const DOMESTIC_GRADIENT = 'linear-gradient(89.99deg, #1B99FF 0%, #7018FF 98.75%)';

export interface RegionChipToggleProps {
  /** 현재 선택된 region. */
  region: HeaderRegion;
  /** 칩 클릭 시 호출(소비처가 URL 네비/로깅 주입). 이미 선택된 칩을 클릭해도 호출한다(해당 region 홈 URL 재로드). */
  onChange: (region: HeaderRegion) => void;
  /** 국내 안내 말풍선 텍스트(예: "최대 5만원 쿠폰이 있어요!"). 해외 selected 시에만 노출. 빈 값이면 미노출. */
  badgeText?: string;
  /**
   * region(주입/URL/쿠키) 확정 여부. 기본 true(하위호환).
   * false 인 동안(첫 렌더~mount effect 전)엔 어느 chip 도 selected 표시하지 않아
   * default('overseas')→확정 region 의 chip flip(덜컥)을 막는다. 확정되면 정답 chip 이 0.15s 페이드로 선택된다.
   * (레이아웃은 고정이라 selected 는 색만 바꿔 이동/덜컥 없음.)
   */
  regionResolved?: boolean;
  className?: string;
}

export default function RegionChipToggle({
  region,
  onChange,
  badgeText,
  regionResolved = true,
  className,
}: RegionChipToggleProps) {
  // 스크롤 시작하면 안내 말풍선 숨김(모바일 RegionToggle 과 동일 동작). 사용자의 스크롤 제스처
  // 자체(wheel=PC / touchmove=터치)를 첫 제스처부터 감지해 즉시 숨기고 리스너를 해제한다.
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const hide = () => {
      setIsScrolled(true);
      // 첫 스크롤 감지 후엔 더 들을 필요 없으므로 즉시 해제.
      window.removeEventListener('touchmove', hide);
      window.removeEventListener('wheel', hide);
    };
    window.addEventListener('touchmove', hide, { passive: true });
    window.addEventListener('wheel', hide, { passive: true });
    return () => {
      window.removeEventListener('touchmove', hide);
      window.removeEventListener('wheel', hide);
    };
  }, []);

  // region 미확정 동안엔 뱃지도 유예(해외 selected 표시 자체를 미룬다).
  const isBadgeVisible = regionResolved && region === 'overseas' && !!badgeText && !isScrolled;

  return (
    <Root className={className}>
      <Container role="tablist" aria-label="해외 국내 전환">
        {REGION_OPTIONS.map(({ label, value }) => {
          // region 확정 전엔 어느 chip 도 selected 하지 않는다(확정 후 정답 chip 만 색으로 페이드인).
          const selected = regionResolved && region === value;
          return (
            <Tab
              key={value}
              type="button"
              role="tab"
              aria-selected={selected}
              chipRegion={value}
              isSelected={selected}
              // 이미 선택된 칩을 클릭해도 onChange 를 호출 → 소비처가 해당 region 홈 URL 로 이동(재로드)한다.
              onClick={() => onChange(value)}
            >
              {label}
            </Tab>
          );
        })}
      </Container>
      {isBadgeVisible && <Bubble role="status">{badgeText}</Bubble>}
    </Root>
  );
}

const Root = styled.div`
  position: relative;
  display: inline-flex;
`;

// §3.1 컨테이너: 120×38 pill (모바일 정본)
const Container = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;

  width: 120px;
  height: 38px;
  padding: 0 2px;
  box-sizing: border-box;
  border: 1px solid #f3f9ff;
  border-radius: 24px;
  background-image: linear-gradient(to right, #ffffff, #f7fcff);
`;

interface TabStyleProps {
  chipRegion: HeaderRegion;
  isSelected: boolean;
}

// §3.3 상태 매트릭스 — chipRegion × isSelected 4가지 (모바일 정본)
const tabVariant = ({ chipRegion, isSelected }: TabStyleProps) => {
  if (chipRegion === 'overseas' && isSelected) {
    // 해외 selected: 다크 단색 + drop-shadow
    return css`
      background-color: #101418;
      background-image: none;
      color: #ffffff;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15);
    `;
  }
  if (chipRegion === 'overseas' && !isSelected) {
    // 해외 unselected (국내 클릭 시): 투명 + Blue/200 단색
    return css`
      background-color: transparent;
      background-image: none;
      color: #a7d4f9;
    `;
  }
  if (chipRegion === 'domestic' && isSelected) {
    // 국내 selected: 그라데이션 배경 + 흰 텍스트
    return css`
      background-color: transparent;
      background-image: ${DOMESTIC_GRADIENT};
      color: #ffffff;
    `;
  }
  // 국내 unselected (해외 클릭 시): 투명 + 그라데이션 텍스트(bg-clip-text)
  return css`
    background-color: transparent;
    background-image: ${DOMESTIC_GRADIENT};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  `;
};

// §3.2 Chip 공통 (모바일 정본 — 해외/국내 폰트 비대칭 유지)
const Tab = styled('button', {
  shouldForwardProp: (prop) => prop !== 'chipRegion' && prop !== 'isSelected',
})<TabStyleProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  height: 32px;
  padding: 6px 10px;
  margin: 0;
  border: none;
  border-radius: 16px;
  cursor: pointer;
  white-space: nowrap;

  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 700;
  line-height: 1.24;
  font-size: ${({ chipRegion }) => (chipRegion === 'overseas' ? '15px' : '16px')};
  letter-spacing: ${({ chipRegion }) => (chipRegion === 'overseas' ? '-0.3px' : '-0.32px')};

  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

  ${tabVariant};

  &:focus-visible {
    outline: 2px solid #1b99ff;
    outline-offset: 2px;
  }
`;

// 모바일 웹 RegionToggle NewBadge 와 동일한 pop 애니메이션(정본).
const badgePop = keyframes`
  0%    { opacity: 0;    transform: translateX(-50%) translateY(16px)    scale(0.35); }
  35%   { opacity: 0.45; transform: translateX(-50%) translateY(-8px)    scale(0.94); }
  55%   { opacity: 0.71; transform: translateX(-50%) translateY(4.8px)   scale(1.28); }
  60%   { opacity: 0.78; transform: translateX(-50%) translateY(8px)     scale(1.17); }
  68.5% { opacity: 0.89; transform: translateX(-50%) translateY(2.9px)   scale(0.991); }
  77%   { opacity: 1;    transform: translateX(-50%) translateY(-2.2px)  scale(1.001); }
  80%   { opacity: 1;    transform: translateX(-50%) translateY(-4px)    scale(1.0045); }
  90.1% { opacity: 1;    transform: translateX(-50%) translateY(-1.98px) scale(0.998); }
  100%  { opacity: 1;    transform: translateX(-50%) translateY(0)       scale(1); }
`;

// 국내 안내 빨간 말풍선 — 해외 selected 전용. 꼬리(▲)가 국내 chip 을 가리킨다(모바일 NewBadge 동일).
const Bubble = styled.span`
  position: absolute;
  top: calc(100% + 2px);
  left: calc(50% + 30px);
  transform: translateX(-50%);
  transform-origin: center center;
  z-index: 1;
  animation: ${badgePop} 0.71s ease-out;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  padding: 0 10px;
  box-sizing: border-box;

  background-color: ${globalVariables.error};
  border-radius: 13px;

  font-family: 'Pretendard', sans-serif;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.26px;
  color: ${globalVariables.white};
  white-space: nowrap;

  pointer-events: none;
  user-select: none;

  &::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-bottom-color: ${globalVariables.error};
  }
`;
